import { Box, Divider, Stack, Typography } from "@mui/material";
import React from "react";
import ColorPicker from "../../../../../../components/ColorPicker/ColorPicker";
import {
  AttributeKey,
  PageUid,
  useCustomisation,
} from "../../../../../../context/CustomisationContext";
import { usePortal } from "../../../../../../context/PortalsContext";
import { updateSectionDesign } from "../../../../../../services/api/apiClient";
import { gray } from "../../../../../../theme/themePrimitives";

interface TextColorsProps {
  pageId?: PageUid;
}

export const TextCustomisation: React.FC<TextColorsProps> = ({
  pageId = "GPC",
}) => {
  const { updateCustomisationSection, getCustomisationValue } =
    useCustomisation();
  const { portals } = usePortal();

  const primaryTextColorKey = `${pageId.toLowerCase()}PrimaryTextColor`;
  const secondaryTextColorKey = `${pageId.toLowerCase()}SecondaryTextColor`;

  const handleAttributeChange = (
    attributeKey: string,
    newAttributeValue?: string
  ) => {
    if (!newAttributeValue) return;

    updateCustomisationSection(pageId, "textColorsSettings", {
      [attributeKey]: newAttributeValue,
    });

    updateSectionDesign(
      portals.selectedPortal?.portalUid!,
      pageId,
      "textColorsSettings",
      {
        attributeKey,
        attributeValue: newAttributeValue,
      }
    );
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5" fontWeight="bold">
          Text colors
        </Typography>
      </Box>
      <Divider />
      <Box
        sx={{
          border: `1px solid ${gray[300]}`,
          borderRadius: "8px",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          p: 2,
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >
          <Typography>Primary text color</Typography>
          <ColorPicker
            value={getCustomisationValue(
              pageId,
              "textColorsSettings",
              primaryTextColorKey as AttributeKey
            )}
            onChange={(color) =>
              handleAttributeChange(primaryTextColorKey, color)
            }
          />
        </Box>
        <Box
          sx={{
            backgroundColor: gray[200],
            borderRadius: "8px",
            height: 200,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <Box>
            <Typography variant="h2">Portal pages</Typography>
            <Typography variant="body2" color="text.secondary">
              Select the pages you want to be visible for people in the user
              portal.
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          border: `1px solid ${gray[300]}`,
          borderRadius: "8px",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          p: 2,
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >
          <Typography>Secondary text color</Typography>
          <ColorPicker
            value={getCustomisationValue(
              pageId,
              "textColorsSettings",
              secondaryTextColorKey as AttributeKey
            )}
            onChange={(color) =>
              handleAttributeChange(secondaryTextColorKey, color)
            }
          />
        </Box>
        <Box
          sx={{
            backgroundColor: gray[200],
            borderRadius: "8px",
            height: 200,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <Box>
            <Typography variant="h2">Portal pages</Typography>
            <Typography variant="body2" color="text.secondary">
              Select the pages you want to be visible for people in the user
              portal.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Stack>
  );
};
